import React from 'react';
import { Box, Checkbox, Divider, FormControlLabel, Typography } from '@mui/material';
import { MessagingToDo } from '@uplift/api';
import { ScrollableView } from '../../Layout';
import { AsyncButton } from '../../Button';
import { asyncWait } from '@uplift/utils';
import { useArchiveMessagingToDoMutation, useCompleteMessagingToDoMutation } from '@uplift/queries';
import { CalendarDateService, DateFormats } from '@uplift/services';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { MessageItem } from '../Messages/MessageBubble.component';


export const ToDoContent: React.FC<{ todo: MessagingToDo }> = ({ todo }) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const archiveMessagingToDoMutation = useArchiveMessagingToDoMutation();
  const completeMessagingToDoMutation = useCompleteMessagingToDoMutation();

  const completeCheckboxLabel = React.useMemo(() => {
    if (todo.completedAt) {
      return `Completed at ${CalendarDateService.format(todo.completedAt, DateFormats.dateTime, undefined, { timezone: timezone })}`;
    }
    if (todo.archivedAt) {
      return `Archived at ${CalendarDateService.format(todo.archivedAt, DateFormats.dateTime, undefined, { timezone: timezone })}`;
    }
    return 'Mark as Completed';
  }, [ todo.archivedAt, todo.completedAt, timezone ]);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box display="flex" gap={1} p={2} alignItems="center">
        <FormControlLabel
          label={completeCheckboxLabel}
          control={(
            <Checkbox
              color="secondary"
              checked={!!todo.completedAt}
              disabled={completeMessagingToDoMutation.isLoading || !!todo.archivedAt}
              onClick={async () => {
                await Promise.all([
                  completeMessagingToDoMutation.mutateAsync({ todoId: todo.id, complete: !todo.completedAt }),
                  asyncWait(600),
                ]);
              }}
            />
          )}
        />
        <Box flex={1} />
        <AsyncButton
          color="error" variant="outlined"
          disabled={!!todo.completedAt}
          onClick={async () => {
            await Promise.all([
              archiveMessagingToDoMutation.mutateAsync({ todoId: todo.id, archive: !todo.archivedAt }),
              asyncWait(600),
            ]);
          }}
        >
          {todo.archivedAt ? 'Unarchive' : 'Archive'}
        </AsyncButton>
      </Box>
      <Divider />
      <ScrollableView width="100%" flex={1}>
        <Box p={2}>
          <Typography>
            {todo.description}
          </Typography>
        </Box>
        <Divider />
        <Box p={2} pb={1}>
          <MessageItem color="secondary" message={todo.message} direction={me.id === todo.message.senderId ? 'send' : 'receive'} />
        </Box>
      </ScrollableView>
    </Box>
  );
};
